/**
 * The whole access process at a glance on `/learn/access` (13 Oct; wide
 * layout 12 Oct, Rusty: "we have this whole screen").
 *
 * A slim sticky bar keeps the lanes as chips, the person's own lit ("You are
 * here"). Below it, open by default and not sticky, the map: the three flows
 * (New user, Change access, Deactivate user) as tabs, one flow per screen,
 * each a lanes × steps grid whose columns share the width (`fr`), so it fits
 * 1366–1920 px with no scrollbar of its own. Only lanes with a step in the
 * flow are drawn. Step boxes wrap their text. A lit lane jumps to its
 * chapter. Below `lg` the grid keeps a minimum width and scrolls sideways.
 *
 * 12 Oct (Rusty): the bar stuck at `top: 0`, under the app header, so it
 * vanished on scroll. It now sticks at `--app-header-height` (the header
 * measures itself, UAT banner included), and its own height is kept in
 * `--learn-bar-height` so a chapter jumped to lands below both.
 */

import { ChevronDown, ChevronUp, Icon, MapPin } from "@norican/icons";
import { Badge, Button, cn, Tabs, Typography } from "@norican/react";
import { useRef, useState } from "react";

import { useHeightVariable } from "@/hooks";
import { useT } from "@/i18n";

import { type Flow, highlightedSteps, lanesFor, type TrainingContent } from "./lib/training";

export interface ProcessMapProps {
  content: TrainingContent;
  roles: string[];
  onLane: (lane: string) => void;
}

function FlowGrid({
  flow,
  content,
  lit,
  onLane,
  litLanes,
}: {
  flow: Flow;
  content: TrainingContent;
  lit: Set<string>;
  litLanes: Set<string>;
  onLane: (lane: string) => void;
}) {
  const { t } = useT();
  // Lanes with no step in this flow are left out, not drawn empty.
  const lanes = content.lanes.filter((lane) => flow.steps.some((s) => s.lane === lane.id));
  const cols = Array.from({ length: flow.cols }, (_, col) => col);
  return (
    <div className="flex flex-col gap-2">
      <Typography as="p" variant="body-sm" tone="muted" lang="en">
        {flow.sub}
      </Typography>
      <div className="overflow-x-auto pb-1 lg:overflow-visible">
        <div
          data-flow={flow.id}
          className="grid min-w-[56rem] gap-1.5 lg:min-w-0"
          style={{
            gridTemplateColumns: `minmax(6.5rem, 8.5rem) repeat(${flow.cols}, minmax(0, 1fr))`,
          }}
        >
          {/* Step order along the top: the flow reads left to right. */}
          <span aria-hidden="true" />
          {cols.map((col) => (
            <span
              key={col}
              aria-hidden="true"
              className="text-fg-muted border-border-subtle border-b pb-0.5 text-xs font-medium"
            >
              {col + 1}
            </span>
          ))}
          {lanes.map((lane) => {
            const laneLit = litLanes.has(lane.id);
            return (
              <div key={lane.id} className="contents">
                <button
                  type="button"
                  onClick={() => onLane(lane.id)}
                  disabled={!laneLit}
                  aria-current={laneLit ? "step" : undefined}
                  title={lane.name}
                  className={cn(
                    "flex items-center gap-1 rounded-md px-2 py-1.5 text-start text-xs font-medium",
                    laneLit
                      ? "bg-accent text-fg-on-accent"
                      : "bg-bg-subtle text-fg-muted cursor-default",
                  )}
                  lang="en"
                >
                  {laneLit ? <Icon icon={MapPin} size="xs" /> : null}
                  {lane.short}
                </button>
                {cols.map((col) => {
                  const steps = flow.steps.filter((s) => s.lane === lane.id && s.col === col);
                  return (
                    <div key={col} className="flex min-w-0 flex-col gap-1">
                      {steps.map((step) => {
                        const on = lit.has(`${flow.id}:${step.id}`);
                        return (
                          <div
                            key={step.id}
                            lang="en"
                            className={cn(
                              "flex-1 rounded-md border px-2 py-1.5 text-xs leading-snug break-words",
                              on
                                ? "border-accent bg-accent-subtle text-fg font-medium"
                                : "border-border bg-bg text-fg-muted",
                              step.kind === "p" || step.kind === "n" ? "border-dashed" : null,
                            )}
                          >
                            {step.t}
                            {step.kind === "m" ? (
                              <span className="text-warning-fg block font-normal">
                                {t("accessTraining.map.byHand")}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ProcessMap({ content, roles, onLane }: ProcessMapProps) {
  const { t } = useT();
  const [open, setOpen] = useState(true);
  const mapRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  useHeightVariable(barRef, "--learn-bar-height");
  const litLanes = lanesFor(roles, content);
  const lit = highlightedSteps(roles, content);
  // Open on the first flow the reader has a part in.
  const firstLit =
    content.flows.find((flow) => flow.steps.some((s) => lit.has(`${flow.id}:${s.id}`)))?.id ??
    content.flows[0]?.id;
  const [flowId, setFlowId] = useState<string | undefined>(undefined);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      // Opened from further down the page: bring the map into view.
      requestAnimationFrame(() =>
        mapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
    }
  };

  return (
    <>
      <div
        ref={barRef}
        data-testid="access-sticky-bar"
        className="border-border bg-bg/95 sticky top-[var(--app-header-height,4rem)] z-10 -mx-4 border-b px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Typography as="span" variant="body-sm" className="font-medium">
            {t("accessTraining.map.title")}
          </Typography>
          <ul className="flex flex-wrap gap-1" aria-label={t("accessTraining.map.title")}>
            {content.lanes.map((lane) => {
              const on = litLanes.has(lane.id);
              return (
                <li key={lane.id}>
                  <button
                    type="button"
                    onClick={() => onLane(lane.id)}
                    disabled={!on}
                    aria-current={on ? "step" : undefined}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
                      on
                        ? "border-accent bg-accent text-fg-on-accent"
                        : "border-border-subtle text-fg-muted cursor-default",
                    )}
                    lang="en"
                  >
                    {on ? <Icon icon={MapPin} size="xs" /> : null}
                    {lane.short}
                  </button>
                </li>
              );
            })}
          </ul>
          {litLanes.size > 0 ? (
            <Badge tone="accent" variant="subtle" size="sm">
              {t("accessTraining.map.youAreHere")}
            </Badge>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="ms-auto"
            aria-expanded={open}
            aria-controls="access-process-map"
            endIcon={<Icon icon={open ? ChevronUp : ChevronDown} size="sm" />}
            onClick={toggle}
          >
            {open ? t("accessTraining.map.hide") : t("accessTraining.map.show")}
          </Button>
        </div>
      </div>
      {open ? (
        <div
          id="access-process-map"
          ref={mapRef}
          className="border-border bg-bg scroll-mt-[calc(var(--app-header-height,4rem)+var(--learn-bar-height,3rem)+0.5rem)] rounded-lg border px-4 pt-2 pb-4"
        >
          <Tabs.Root value={flowId ?? firstLit} onValueChange={(value) => setFlowId(String(value))}>
            <Tabs.List aria-label={t("accessTraining.map.title")}>
              {content.flows.map((flow) => (
                <Tabs.Tab key={flow.id} value={flow.id}>
                  <span lang="en">{flow.title}</span>
                </Tabs.Tab>
              ))}
              <Tabs.Indicator />
            </Tabs.List>
            {content.flows.map((flow) => (
              <Tabs.Panel key={flow.id} value={flow.id} className="pt-3">
                <FlowGrid
                  flow={flow}
                  content={content}
                  lit={lit}
                  litLanes={litLanes}
                  onLane={onLane}
                />
              </Tabs.Panel>
            ))}
          </Tabs.Root>
        </div>
      ) : null}
    </>
  );
}
